import { DocumentInfoSchema } from "@peepel/api/schemas/document";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getApiClient, getSessionCookieHeader } from "~/utils/api-client";

const DOCUMENTS_KEYS = {
	all: ["documents"] as const,
	lists: () => [...DOCUMENTS_KEYS.all, "list"] as const,
	self: () => [...DOCUMENTS_KEYS.lists(), "self"] as const,
};

const documentsQueryOptions = queryOptions({
	queryKey: DOCUMENTS_KEYS.self(),
	queryFn: () => fetchDocuments(),
});

const fetchDocuments = createServerFn({ method: "GET" }).handler(async () => {
	const api_client = getApiClient(getSessionCookieHeader());
	const result = await api_client.self.api.documents.$get();

	if (!result.ok) {
		throw new Error("Failed to fetch documents");
	}

	const payload = await result.json();
	return DocumentInfoSchema.array().parse(payload);
});

const fetchDocumentDownloadUrl = createServerFn({ method: "GET" })
	.validator(z.number().int().positive())
	.handler(async ({ data: doc_id }) => {
		const api_client = getApiClient(getSessionCookieHeader());
		const result = await api_client.self.api.documents[":doc_id"].download.$get(
			{
				param: { doc_id: String(doc_id) },
			},
		);

		if (!result.ok) {
			if (result.status === 404) {
				throw new Error("Document not found");
			}
			if (result.status === 403) {
				throw new Error("Access denied");
			}
			if (result.status === 503) {
				throw new Error("Download not available");
			}
			throw new Error("Failed to get download URL");
		}

		const payload = await result.json();

		return payload as { download_url: string };
	});

export { documentsQueryOptions, fetchDocumentDownloadUrl };
